import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import AdminContext from '../Context/Admin/adminContext';
import CbmContext from '../Context/Cbm/cbmContext';
import usePermissions from './usePermissions';

/**
 * Custom hook for Admin Dashboard logic
 *
 * Encapsulates all dashboard-specific state and data fetching:
 * - Stats calculation (users, forms, stores, sessions)
 * - Data fetching based on permissions
 * - Loading state management
 * - Trend calculations (recent activity)
 *
 * @returns {Object} Dashboard state and actions
 */
export const useAdminDashboard = ({ formsCount } = {}) => {
  const adminContext = useContext(AdminContext);
  const cbmContext = useContext(CbmContext);

  const {
    submittedForms,
    recentForms,
    totalElements,
    portalUserList,
    getAllUsers,
    getAllStores: fetchAllStores,
    activeSessions,
    getAllSessions,
    sessionStats,
  } = adminContext;

  const { stores, getStores } = cbmContext;

  const { portalUser, isAdmin, hasOfficeAccess, isDM, isAS } = usePermissions();

  const hasFetchedData = useRef(false);
  const [allStores, setAllStores] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Calculate dashboard stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalForms: 0,
    totalStores: 0,
    activeSessions: 0,
    recentFormsCount: 0,
    averageFormsPerDay: 0,
  });

  // Trend data for charts/displays
  const [trends, setTrends] = useState({
    formsLast24h: 0,
    formsLast7d: 0,
    newUsersThisMonth: 0,
    sessionsPeak: 0,
  });

  /**
   * Calculate recent forms count within a time window
   */
  const calculateRecentFormsCount = useCallback(
    (hours = 24) => {
      if (!recentForms || !Array.isArray(recentForms)) return 0;
      const cutoff = new Date();
      cutoff.setHours(cutoff.getHours() - hours);

      return recentForms.reduce((count, form) => {
        const createdDate = new Date(form.created || form.submittedDate || 0);
        return createdDate > cutoff ? count + 1 : count;
      }, 0);
    },
    [recentForms]
  );

  /**
   * Calculate forms submitted in the last N days
   */
  const calculateFormsInDays = useCallback(
    (days = 7) => {
      if (!recentForms || !Array.isArray(recentForms)) return 0;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);

      return recentForms.reduce((count, form) => {
        const createdDate = new Date(form.created || form.submittedDate || 0);
        return createdDate > cutoff ? count + 1 : count;
      }, 0);
    },
    [recentForms]
  );

  /**
   * Fetch all dashboard data based on user permissions
   */
  const fetchDashboardData = useCallback(
    async (force = false) => {
      if (!portalUser) return;
      if (!force && hasFetchedData.current) return;

      hasFetchedData.current = true;
      setIsLoading(true);

      try {
        const promises = [];

        // Admin-only data
        if (isAdmin) {
          if (!portalUserList) {
            promises.push(getAllUsers());
          }
          promises.push(getAllSessions());
        }

        // Admin/Office can see all stores
        if (isAdmin || hasOfficeAccess) {
          promises.push(
            fetchAllStores().then((data) => {
              setAllStores(data);
              return data;
            })
          );
        } else if (isDM && portalUser?.district) {
          // DM users see only their district stores
          if (!stores || stores.length === 0) {
            promises.push(getStores(portalUser.district));
          }
        }

        await Promise.allSettled(promises);
        setLastRefreshed(new Date());
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      portalUser,
      isAdmin,
      hasOfficeAccess,
      isDM,
      portalUserList,
      stores,
      getAllUsers,
      getAllSessions,
      fetchAllStores,
      getStores,
    ]
  );

  /**
   * Refresh all dashboard data
   */
  const refreshData = useCallback(async () => {
    hasFetchedData.current = false;
    await fetchDashboardData(true);
  }, [fetchDashboardData]);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Update stats when data changes
  useEffect(() => {
    const totalForms =
      typeof formsCount === 'number'
        ? formsCount
        : typeof totalElements === 'number'
        ? totalElements
        : 0;

    const storeCount =
      isAdmin || hasOfficeAccess ? allStores?.length || 0 : stores?.length || 0;

    const recentFormsCount = calculateRecentFormsCount(24);
    const formsLast7d = calculateFormsInDays(7);

    setStats({
      totalUsers: portalUserList?.length || 0,
      totalForms,
      totalStores: storeCount,
      activeSessions: activeSessions?.length || 0,
      recentFormsCount,
      averageFormsPerDay: formsLast7d > 0 ? Math.round(formsLast7d / 7) : 0,
    });

    setTrends({
      formsLast24h: recentFormsCount,
      formsLast7d,
      newUsersThisMonth: 0, // Would require user creation date tracking
      sessionsPeak:
        sessionStats?.totalActiveSessions || activeSessions?.length || 0,
    });
  }, [
    submittedForms,
    portalUserList,
    allStores,
    stores,
    activeSessions,
    sessionStats,
    isAdmin,
    hasOfficeAccess,
    formsCount,
    totalElements,
    calculateRecentFormsCount,
    calculateFormsInDays,
  ]);

  /**
   * Get formatted time since last refresh
   */
  const getTimeSinceRefresh = useCallback(() => {
    if (!lastRefreshed) return 'Never';
    const now = new Date();
    const diff = Math.floor((now - lastRefreshed) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return lastRefreshed.toLocaleDateString();
  }, [lastRefreshed]);

  /**
   * Get greeting based on time of day
   */
  const getGreeting = useCallback(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }, []);

  /**
   * Get role-specific welcome message
   */
  const getWelcomeMessage = useCallback(() => {
    const name = portalUser?.userFirst || 'Admin';
    const greeting = getGreeting();

    if (isAdmin) {
      return `${greeting}, ${name}. You have full administrative access.`;
    }
    if (hasOfficeAccess) {
      return `${greeting}, ${name}. Welcome to the office portal.`;
    }
    if (isDM) {
      const district = portalUser?.district || 'your district';
      return `${greeting}, ${name}. Managing ${district}.`;
    }
    if (isAS) {
      return `${greeting}, ${name}. Managing your assigned sub-areas.`;
    }
    return `${greeting}, ${name}.`;
  }, [portalUser, isAdmin, hasOfficeAccess, isDM, isAS, getGreeting]);

  const getScopeLabel = useCallback(() => {
    if (isAdmin || hasOfficeAccess) {
      return 'Viewing: All Districts';
    }

    if (isDM) {
      const districtName = Array.isArray(portalUser?.district)
        ? portalUser?.district?.[0]?.districtName
        : portalUser?.district?.districtName || portalUser?.district || null;
      return districtName
        ? `Viewing: District - ${districtName}`
        : 'Viewing: District Scope';
    }

    if (isAS) {
      const subArea = portalUser?.assignedSubAreas?.[0];
      const subAreaName = subArea?.name;
      const districtName = subArea?.district?.districtName;
      if (subAreaName && districtName) {
        return `Viewing: Sub-Area - ${subAreaName} (${districtName})`;
      }
      if (subAreaName) {
        return `Viewing: Sub-Area - ${subAreaName}`;
      }
      return 'Viewing: Sub-Area Scope';
    }

    return null;
  }, [portalUser, isAdmin, hasOfficeAccess, isDM, isAS]);

  return {
    // State
    stats,
    trends,
    isLoading,
    lastRefreshed,
    allStores,
    recentForms,
    activeSessions,
    sessionStats,
    portalUserList,

    // Permissions (re-exported for convenience)
    portalUser,
    isAdmin,
    hasOfficeAccess,
    isDM,
    isAS,

    // Actions
    refreshData,

    // Computed values
    getTimeSinceRefresh,
    getGreeting,
    getWelcomeMessage,
    getScopeLabel,

    // Utilities
    calculateRecentFormsCount,
    calculateFormsInDays,
  };
};

export default useAdminDashboard;
